import { useQuery, useQueryClient } from '@tanstack/react-query';
import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags';

import {
	fetchAvailableOrgs,
	fetchAvailableWorkspaces,
	getAvailableWorkspacesQueryKey,
	getRecentWorkspaceForUser,
	getRecentWorkspaceForUserQueryKey,
	getWorkspaceIdBySiteId,
	getWorkspaceIdBySiteIdQueryKey,
} from '../../services';

export interface WorkspaceContext {
	workspaces: string[];
	isReady: boolean;
	error?: Error;
}

/**
 * Generates a query key for workspace context.
 */
export const getWorkspaceContextQueryKey = (
	workspaceId: string | undefined,
	siteId: string | undefined,
	atlassianAccountId: string,
): string[] => {
	const parts: string[] = ['workspace-context'];
	if (workspaceId) {
		parts.push(workspaceId);
	}
	if (siteId) {
		parts.push(siteId);
	}
	parts.push(atlassianAccountId);
	return parts;
};

/**
 * Fetches workspace context based on workspace and site IDs.
 *
 * @param queryClient - React Query client for fetching data
 * @param workspaceId - The workspace ID from URL (optional)
 * @param siteId - The site ID from URL (optional)
 * @param atlassianAccountId
 * @returns Promise resolving to WorkspaceContext
 */
async function fetchWorkspaceContext(
	queryClient: ReturnType<typeof useQueryClient>,
	workspaceId: string | undefined,
	siteId: string | undefined,
	atlassianAccountId: string,
): Promise<WorkspaceContext> {
	if (workspaceId) {
		// Scenario 1: v2 url with workspace id
		// Assume URL workspace ID is valid
		return {
			workspaces: [workspaceId],
			isReady: true,
			error: undefined,
		};
	} else if (siteId) {
		// Scenario 2: v1 url with site id

		// this returns an empty string for now
		const workspaceResponse = await queryClient.fetchQuery({
			queryKey: getWorkspaceIdBySiteIdQueryKey(siteId),
			queryFn: () => getWorkspaceIdBySiteId(siteId),
			staleTime: Infinity,
		});

		const workspaceId = workspaceResponse.id;

		return {
			workspaces: workspaceId ? [workspaceId] : [],
			isReady: true,
		};
	} else {
		// Scenario 3: Root URL (no workspaceId, no siteId)
		const recentWorkspaces = await queryClient.fetchQuery({
			queryKey: getRecentWorkspaceForUserQueryKey(atlassianAccountId),
			queryFn: () => getRecentWorkspaceForUser(atlassianAccountId),
			staleTime: Infinity,
		});
		if (recentWorkspaces.length > 0) {
			return {
				workspaces: recentWorkspaces,
				isReady: true,
				error: undefined,
			};
		}

		const workspacesResponse = await queryClient.fetchQuery({
			queryKey: getAvailableWorkspacesQueryKey([]),
			queryFn: () => fetchAvailableWorkspaces([]),
			staleTime: Infinity,
		});
		const workspaces = Object.values(workspacesResponse).filter(Boolean);
		return {
			workspaces,
			isReady: true,
			error: undefined,
		};
	}
}

/**
 * Hook that gathers workspace context based on current url and whether v2 is enabled for the org.
 *
 * @param workspaceId - The workspace ID from URL (optional)
 * @param siteId - The site ID from URL (optional)
 * @param atlassianAccountId
 * @param isFeatureGateInitialized
 * @returns WorkspaceContext with workspaces and ready flag to indicate if the app is ready to render
 */
export const useWorkspaceContext = (
	workspaceId: string | undefined,
	siteId: string | undefined,
	atlassianAccountId: string,
	isFeatureGateInitialized: boolean = false,
): WorkspaceContext => {
	di(
		useQuery,
		useQueryClient,
		getWorkspaceIdBySiteId,
		getRecentWorkspaceForUser,
		fetchAvailableOrgs,
		fetchAvailableWorkspaces,
	);

	const queryClient = useQueryClient();
	const isAppSelectionChangeEnabled =
		isFeatureGateInitialized && fg('asf620_route_arch_and_app_selection_changes');

	const { data, isLoading, error, isFetching } = useQuery({
		queryKey: getWorkspaceContextQueryKey(workspaceId, siteId, atlassianAccountId),
		queryFn: () => fetchWorkspaceContext(queryClient, workspaceId, siteId, atlassianAccountId),
		enabled: isAppSelectionChangeEnabled,
		staleTime: Infinity,
	});

	// error state (will fallback to v1 for now)
	if (error) {
		return {
			workspaces: [],
			isReady: true,
			error: error instanceof Error ? error : new Error(String(error)),
		};
	}

	// loading state
	if (!isAppSelectionChangeEnabled || isLoading || isFetching || !data) {
		return {
			workspaces: [],
			isReady: false,
			error: undefined,
		};
	}

	return data;
};
